import React, { createContext, useContext, useRef, useState } from "react";
import { createStore, useStore } from 'zustand'
import ReactToolTip from 'react-tooltip';
import { generateId } from '../utils/genIds';
import InvoiceScheduleForm from './InvoiceScheduleForm';
import { Radio, RadioGroup, Transition } from '@headlessui/react'
import { CheckCircleIcon, CurrencyDollarIcon, UsersIcon, CalendarDaysIcon, DocumentTextIcon, GlobeAltIcon } from '@heroicons/react/20/solid'

export const FormContext = createContext()

const duesCalculationOptions = [
  {
    id: "flatrate",
    title: 'Flat Rate',
    description: 'All members pay the same dues amount',
    icon: CurrencyDollarIcon
  },
  {
    id: "individual",
    title: 'Individual',
    description: 'Each member pays a custom amount',
    icon: UsersIcon
  }
]

const createFundStore = (initialProps) => {
  const defaultProps = {
    invoicingFund: {},
    dueDayOptions: [],
    scheduledReminders: [],
    duesCalculationOptions: duesCalculationOptions
  }
  const selectedDuesCalculation = duesCalculationOptions.find(duesCalculation => duesCalculation.id === initialProps.invoicingFund.dues_calculation_type) || duesCalculationOptions[0]
  return createStore()(set => (
    {
      ...defaultProps,
      ...initialProps,
      setSelectedDuesCalculation: (duesCalculation) => set({ selectedDuesCalculation: duesCalculation }),
      setAutoRemind: (autoRemind) => set((state) => ({ invoicingFund: { ...state.invoicingFund, auto_remind: autoRemind } })),
      setAutoInvoice: (autoInvoice) => set((state) => ({ invoicingFund: { ...state.invoicingFund, auto_invoice: autoInvoice } })),
      selectedDuesCalculation: selectedDuesCalculation,
    }))
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const CreateInvoiceFundFormProvider = (props) => {
  const formStore = useRef(createFundStore(props)).current
  return (
    <FormContext.Provider value={formStore}>
      <CreateInvoiceFundForm />
    </FormContext.Provider>
  )
}

const SectionHeader = ({ icon: Icon, title, description, color = "indigo" }) => {
  const colorClasses = {
    indigo: "bg-indigo-100 text-indigo-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    blue: "bg-blue-100 text-blue-600",
  }

  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
    </div>
  )
}

const CreateInvoiceFundForm = () => {
  const store = useContext(FormContext)
  const { invoicingFund, organization, dueDayOptions, scheduledReminders, selectedDuesCalculation } = useStore(store)

  return (
    <div className="space-y-8">
      {/* Hidden Fields */}
      {invoicingFund.id && <input type="hidden" name="fund[id]" value={invoicingFund.id} />}
      {invoicingFund.id && <input type="hidden" name="_method" value="put" />}
      <input type="hidden" name="fund[status]" value="active" />
      {organization.id && <input type="hidden" name="fund[organization_id]" value={organization.id} />}
      <input type="hidden" name="fund[fund_type]" value="invoice" />

      {/* Section 1: Basic Information */}
      <div className="space-y-4">
        <SectionHeader
          icon={DocumentTextIcon}
          title="Basic Information"
          description="Set the name for this membership group"
          color="indigo"
        />
        <div className="pl-13">
          <TextInput
            label="Group Name"
            required
            type="text"
            name="fund[name]"
            defaultValue={invoicingFund.name}
            placeholder="e.g., Monthly Dues, Annual Membership"
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Section 2: Dues Calculation */}
      <div className="space-y-4">
        <SectionHeader
          icon={CurrencyDollarIcon}
          title="Dues Configuration"
          description="Choose how dues are calculated for members"
          color="emerald"
        />
        <div className="space-y-4">
          <RadioInputs />

          <div className="overflow-hidden">
            <Transition
              as="div"
              show={selectedDuesCalculation?.id === 'flatrate'}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 -translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 -translate-y-2"
            >
              <div className="pt-4 pl-4 border-l-2 border-emerald-200 ml-4">
                <TextInput
                  label="Dues Amount"
                  required
                  toolTip="This is the dollar amount that all members will pay"
                  type="currency"
                  name="fund[amount]"
                  defaultValue={invoicingFund.amount}
                  placeholder="0.00"
                />
              </div>
            </Transition>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Section 3: Invoice Schedule */}
      <div>
        <InvoiceScheduleForm
          invoicing_fund={invoicingFund}
          scheduled_reminders={scheduledReminders}
          due_day_options={dueDayOptions}
        />
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Section 4: Public Page Visibility */}
      <div className="space-y-4">
        <SectionHeader
          icon={GlobeAltIcon}
          title="Public Page"
          description="Show this dues group on your organization's public page"
          color="blue"
        />
        <div className="pl-13">
          <PublicPageToggle defaultChecked={invoicingFund.show_on_public_page} />
        </div>
      </div>
    </div>
  );
};

const TextInput = (props) => {
  const { label, required, type, name, defaultValue, onChange, toolTip, description, options, placeholder, rows = 3 } = props
  const id = generateId()
  let fundsTypeOptions = [];
  if (type === 'select') {
    fundsTypeOptions = Object.keys(options).map((e, i) => {
      return (
        <option key={i} value={options[e]}>
          {e.split('_').join(' ')}
        </option>
      );
    });
  }

  const baseInputClasses = "block w-full rounded-lg border-0 py-2.5 px-3.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all duration-150"

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium leading-6 text-gray-700 mb-1.5"
        >
          {label}
          {!!required && <span className="text-red-500 ml-0.5">*</span>}
          {!!toolTip && <ToolTip dataForId={id} dataTip={toolTip} />}
        </label>
      )}
      {!!description && (
        <p className="mb-2 text-sm text-gray-500">{description}</p>
      )}
      <div className="relative">
        {type === "select" && (
          <select
            required={required}
            id={id}
            name={name}
            defaultValue={defaultValue}
            onChange={onChange}
            className={baseInputClasses}
          >
            {fundsTypeOptions}
          </select>
        )}

        {type === "text" && (
          <input
            required={required}
            id={id}
            type="text"
            name={name}
            defaultValue={defaultValue}
            onChange={onChange}
            placeholder={placeholder}
            className={baseInputClasses}
          />
        )}

        {type === "currency" && (
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              required={required}
              id={id}
              type="number"
              name={name}
              step="0.01"
              min="0"
              defaultValue={defaultValue}
              onChange={onChange}
              placeholder={placeholder}
              className={`${baseInputClasses} pl-7`}
            />
          </div>
        )}

        {type === "textarea" && (
          <textarea
            required={required}
            id={id}
            name={name}
            rows={rows}
            defaultValue={defaultValue}
            onChange={onChange}
            placeholder={placeholder}
            className={`${baseInputClasses} resize-none`}
          />
        )}
      </div>
    </div>
  )
}

const ToolTip = (props) => {
  const { dataForId, dataTip } = props;

  return (
    <span data-for={dataForId} data-tip={dataTip} className="ml-1.5 inline-flex cursor-help">
      <svg viewBox="0 0 20 20" className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
      <ReactToolTip
        multiline
        id={dataForId}
        type="dark"
        place="top"
        effect="solid"
        className="!bg-gray-900 !text-white !rounded-lg !px-3 !py-2 !text-xs !max-w-xs"
      />
    </span>
  );
};



const RadioInputs = (props) => {
  const store = useContext(FormContext)
  const { duesCalculationOptions, selectedDuesCalculation } = useStore(store)
  const setSelectedDuesCalculation = useStore(store, (s) => s.setSelectedDuesCalculation)

  return (
    <fieldset>
      <input type="hidden" name="fund[dues_calculation_type]" value={selectedDuesCalculation?.id || 'flatrate'} />
      <RadioGroup
        value={selectedDuesCalculation}
        onChange={setSelectedDuesCalculation}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {duesCalculationOptions.map((option) => {
          const Icon = option.icon
          return (
            <Radio
              key={option.id}
              value={option}
              aria-label={option.title}
              aria-description={option.description}
              className={({ checked }) => classNames(
                checked
                  ? 'border-emerald-500 ring-2 ring-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50',
                'group relative flex cursor-pointer rounded-xl border-2 p-4 transition-all duration-150 focus:outline-none'
              )}
            >
              {({ checked }) => (
                <>
                  <div className="flex items-start gap-4 w-full">
                    <div className={classNames(
                      checked ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200',
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors'
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={classNames(
                          checked ? 'text-emerald-900' : 'text-gray-900',
                          'text-sm font-semibold'
                        )}>
                          {option.title}
                        </span>
                        {checked && (
                          <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        )}
                      </div>
                      <span className={classNames(
                        checked ? 'text-emerald-700' : 'text-gray-500',
                        'text-sm mt-0.5 block'
                      )}>
                        {option.description}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </Radio>
          )
        })}
      </RadioGroup>
    </fieldset>
  )
}

const PublicPageToggle = ({ defaultChecked = false }) => {
  const [enabled, setEnabled] = useState(defaultChecked || false)

  return (
    <div className="flex items-start gap-3">
      <input type="hidden" name="fund[show_on_public_page]" value={enabled ? "1" : "0"} />
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => setEnabled(!enabled)}
        className={classNames(
          enabled ? 'bg-blue-600' : 'bg-gray-200',
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
        )}
      >
        <span
          className={classNames(
            enabled ? 'translate-x-5' : 'translate-x-0',
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
          )}
        />
      </button>
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-900">Show on public page</span>
        <p className="text-sm text-gray-500 mt-0.5">
          Display this dues group on your organization's public page so visitors can find information about membership and how to pay dues.
        </p>
      </div>
    </div>
  )
}

export default CreateInvoiceFundFormProvider;